import { useEffect, useState } from "react";
import { collection, getDocs, query, where, orderBy } from "firebase/firestore";
import { db } from "../../firebase";
import ProfileAvatarLink from "./ProfileAvatarLink";
import ProfileNameLink from "./ProfileNameLink";

const STROKE = 1.8;

function WallNote({ note }) {
  const styleClass =
    note.displayStyle === "handwritten"
      ? "cp-aw-note--hand"
      : note.displayStyle === "warm"
        ? "cp-aw-note--warm"
        : "cp-aw-note--clean";

  const fanId = note.fanId || note.fromUserId || note.userId;

  return (
    <article className={`cp-aw-note ${styleClass}${note.status === "pinned" ? " cp-aw-note--pinned" : ""}`}>
      {note.status === "pinned" && (
        <span className="cp-aw-pin">
          <svg width="10" height="10" viewBox="0 0 24 24" fill="currentColor" stroke="none" aria-hidden>
            <path d="M12 2l2.4 7.4H22l-6.2 4.5 2.4 7.4L12 17l-6.2 4.3 2.4-7.4L2 9.4h7.6z" />
          </svg>
          Pinned
        </span>
      )}
      <p className="cp-aw-text">{note.text}</p>
      <footer className="cp-aw-foot">
        <span className="cp-aw-from">
          {fanId ? (
            <ProfileAvatarLink
              userId={fanId}
              src={note.fromAvatar || note.avatar}
              alt={note.fromName || "Fan"}
              className="cp-aw-from-link"
              imgClassName="cp-aw-from-av"
              size="sm"
              pinnedTrophy={note.fromPinnedTrophy}
            />
          ) : null}
          —{" "}
          {fanId ? (
            <ProfileNameLink userId={fanId} className="cp-aw-from-name">
              {note.fromName || "Anonymous fan"}
            </ProfileNameLink>
          ) : (
            note.fromName || "Anonymous fan"
          )}
        </span>
        {note.pinnedAt?.seconds && (
          <time className="cp-aw-date">
            {new Date(note.pinnedAt.seconds * 1000).toLocaleDateString("en-CA", {
              month: "short",
              day: "numeric",
              year: "numeric",
            })}
          </time>
        )}
      </footer>
    </article>
  );
}

export default function AppreciationWallTab({ creatorId, isOwner, pendingCount = 0 }) {
  const [notes, setNotes] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!creatorId) return;
    let cancelled = false;

    const load = async () => {
      setLoading(true);
      try {
        const notesRef = collection(db, "appreciation_wall", creatorId, "notes");
        const snap = await getDocs(
          query(notesRef, where("status", "in", ["approved", "pinned"]), orderBy("pinnedAt", "desc"))
        ).catch(async () => {
          return getDocs(query(notesRef, where("status", "in", ["approved", "pinned"])));
        });

        if (cancelled) return;
        const items = snap.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .sort((a, b) => {
            if (a.status === "pinned" && b.status !== "pinned") return -1;
            if (b.status === "pinned" && a.status !== "pinned") return 1;
            return (b.pinnedAt?.seconds || b.createdAt?.seconds || 0) - (a.pinnedAt?.seconds || a.createdAt?.seconds || 0);
          });
        setNotes(items);
      } catch (err) {
        console.error("Appreciation wall load error:", err);
        if (!cancelled) setNotes([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    load();
    return () => { cancelled = true; };
  }, [creatorId]);

  if (loading) {
    return <div className="cp-aw-loading">Loading appreciation notes…</div>;
  }

  if (notes.length === 0) {
    return (
      <div className="cp-aw-empty">
        <div className="cp-aw-empty-icon">
          <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="var(--gold)" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
            <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z" />
            <polyline points="22,6 12,13 2,6" />
          </svg>
        </div>
        <h3>No notes on the wall yet</h3>
        <p>Fans can send heartfelt notes that appear here once approved.</p>
        {isOwner && pendingCount > 0 && (
          <span className="cp-aw-pending">{pendingCount} note{pendingCount !== 1 ? "s" : ""} awaiting review</span>
        )}
      </div>
    );
  }

  return (
    <div className="cp-aw-grid">
      {isOwner && pendingCount > 0 && (
        <div className="cp-aw-owner-banner">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={STROKE} strokeLinecap="round" strokeLinejoin="round">
            <circle cx="12" cy="12" r="10" /><line x1="12" y1="8" x2="12" y2="12" /><line x1="12" y1="16" x2="12.01" y2="16" />
          </svg>
          {pendingCount} pending note{pendingCount !== 1 ? "s" : ""} to review
        </div>
      )}
      {notes.map((note) => (
        <WallNote key={note.id} note={note} />
      ))}
    </div>
  );
}

export async function countPendingWallNotes(creatorId) {
  if (!creatorId) return 0;
  try {
    const snap = await getDocs(
      query(
        collection(db, "appreciation_wall", creatorId, "notes"),
        where("status", "==", "pending")
      )
    );
    return snap.size;
  } catch {
    return 0;
  }
}
